import { ActionContext } from '../value-objects/ActionContext';
import { EnergyLevel } from '../value-objects/EnergyLevel';
import { DomainEvent } from '../events/DomainEvent';
import { NextActionCreated, TaskAssigned } from '../events/GTDEvents';
import { 
  AssignmentError, 
  ThoughtProcessingError 
} from '../errors/GTDDomainError';

/**
 * GTD Next Action - the fundamental unit of actionable work
 * "The next physical action required to move something forward" - David Allen
 * 
 * Core GTD Principle: Actions must be specific, actionable, and context-based
 */
export class NextAction {
  private domainEvents: DomainEvent[] = [];
  private _completedAt?: Date;
  private _assignedTo?: string;
  private _assignedAt?: Date;
  private _roleType?: string;

  constructor(
    public readonly id: string,
    public readonly description: string,
    public readonly context: ActionContext,
    public readonly energyRequired: EnergyLevel,
    public readonly estimatedMinutes: number,
    public readonly createdBy: string,
    public readonly createdAt: Date = new Date(),
    public readonly projectId?: string
  ) {
    this.validateAction(description, estimatedMinutes);
    
    this.addDomainEvent(new NextActionCreated(
      id, 
      description, 
      context.toString(), 
      energyRequired.toString(), 
      createdBy
    ));
  }

  /**
   * Create a new next action with validation
   */
  static create(
    description: string,
    context: ActionContext,
    energy: EnergyLevel,
    minutes: number,
    createdBy: string,
    projectId?: string
  ): NextAction {
    const id = this.generateId();
    return new NextAction(id, description, context, energy, minutes, createdBy, new Date(), projectId);
  }

  /**
   * Reconstitute from persistence
   */
  static reconstitute(
    id: string,
    description: string,
    context: ActionContext,
    energy: EnergyLevel,
    minutes: number,
    createdBy: string,
    createdAt: Date,
    projectId?: string,
    assignedTo?: string,
    assignedAt?: Date,
    roleType?: string,
    completedAt?: Date
  ): NextAction {
    const action = new NextAction(id, description, context, energy, minutes, createdBy, createdAt, projectId);
    
    // Clear events from constructor
    action.clearDomainEvents();
    
    // Restore state
    if (assignedTo && assignedAt && roleType) {
      action._assignedTo = assignedTo;
      action._assignedAt = assignedAt;
      action._roleType = roleType;
    }
    
    if (completedAt) {
      action._completedAt = completedAt;
    }
    
    return action;
  }

  /**
   * Assign this action to a developer/team member
   * Integrates with Party Management domain
   */
  assignTo(partyId: string, roleType: string): void {
    if (this.isCompleted()) {
      throw new AssignmentError('Cannot assign completed actions', this.id);
    }

    if (this.isAssigned()) {
      throw new AssignmentError(
        `Action is already assigned to ${this._assignedTo}`, 
        this.id
      );
    }

    if (!partyId?.trim()) {
      throw new AssignmentError('Party ID is required for assignment', this.id);
    }

    if (!roleType?.trim()) {
      throw new AssignmentError('Role type is required for assignment', this.id);
    }

    this._assignedTo = partyId;
    this._roleType = roleType;
    this._assignedAt = new Date();
    
    this.addDomainEvent(new TaskAssigned(
      this.id, 
      partyId, 
      roleType, 
      this._assignedAt.toISOString()
    ));
  }

  /**
   * Unassign the action (return to available pool)
   */
  unassign(): void {
    if (!this.isAssigned()) {
      throw new AssignmentError('Action is not currently assigned', this.id);
    }

    if (this.isCompleted()) {
      throw new AssignmentError('Cannot unassign completed actions', this.id);
    }

    this._assignedTo = undefined;
    this._roleType = undefined;
    this._assignedAt = undefined;
  }

  /**
   * Mark action as completed
   */
  complete(): void {
    if (this.isCompleted()) {
      throw new ThoughtProcessingError('Action is already completed');
    }

    this._completedAt = new Date();
  }

  /**
   * Check if action can be performed given current context and energy
   * Core GTD principle: Match actions to available resources
   */
  canBePerformedWith(availableContext: ActionContext, availableEnergy: EnergyLevel): boolean {
    if (this.isCompleted()) {
      return false;
    }

    const contextMatch = this.context.equals(availableContext);
    const energyMatch = this.energyRequired.canBePerformedWhen(availableEnergy);
    
    return contextMatch && energyMatch;
  }

  /**
   * Get available actions that match criteria
   */
  static getAvailableActions(
    actions: NextAction[], 
    context: ActionContext, 
    energy: EnergyLevel
  ): NextAction[] {
    return actions.filter(action => 
      !action.isCompleted() && 
      !action.isAssigned() && 
      action.canBePerformedWith(context, energy)
    );
  }

  // State queries
  isCompleted(): boolean {
    return this._completedAt !== undefined;
  }

  isAssigned(): boolean {
    return this._assignedTo !== undefined;
  }

  isAvailable(): boolean {
    return !this.isCompleted() && !this.isAssigned();
  }

  // Getters
  get assignedTo(): string | undefined {
    return this._assignedTo;
  }

  get roleType(): string | undefined {
    return this._roleType;
  }

  get assignedAt(): Date | undefined {
    return this._assignedAt;
  }

  get completedAt(): Date | undefined {
    return this._completedAt;
  }

  get status(): 'available' | 'assigned' | 'completed' {
    if (this.isCompleted()) return 'completed';
    if (this.isAssigned()) return 'assigned';
    return 'available';
  }

  // Event sourcing support
  getDomainEvents(): DomainEvent[] {
    return [...this.domainEvents];
  }

  clearDomainEvents(): void {
    this.domainEvents = [];
  }

  // Private methods
  private validateAction(description: string, estimatedMinutes: number): void {
    if (!description?.trim()) {
      throw new ThoughtProcessingError('Action description is required');
    }

    if (estimatedMinutes <= 0) {
      throw new ThoughtProcessingError('Estimated minutes must be positive');
    }

    // No upper limit validation - NextActions can take any reasonable duration
    // The 2-minute rule is workflow guidance, not a domain constraint
  }

  private addDomainEvent(event: DomainEvent): void {
    this.domainEvents.push(event);
  }

  private static generateId(): string {
    return `action-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }
}